import React, { Component, useState } from "react";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { stripePromise } from "../../../stripe";
import axiosInstance from "../../../axios-instance";
import { getAdminBusinessId } from "../../../utils/authHelper";

/* ─── Design tokens (match SMS Balance UI mock) ───────────────────────── */
const C = {
  navy: "#0B3A5C",
  navyMid: "#0E4A73",
  orange: "#F97316",
  orangeHover: "#EA580C",
  orangeSoft: "#FFF7ED",
  orangeBorder: "#FDBA74",
  blueSoft: "#E8F4FC",
  blueText: "#0C4A6E",
  white: "#FFFFFF",
  dark: "#111827",
  mid: "#374151",
  muted: "#6B7280",
  border: "#E5E7EB",
  surface: "#F8FAFC",
  green: "#10B981",
  greenBg: "#D1FAE5",
  greenText: "#065F46",
  blueBadge: "#DBEAFE",
  blueBadgeText: "#1E40AF",
  danger: "#EF4444",
  dangerBg: "#FEE2E2",
};

const fmtUsd = (n) =>
  `US$ ${Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const fmtSms = (n) => Number(n || 0).toLocaleString();

const CARD_OPTS = {
  style: {
    base: {
      fontSize: "15px",
      color: C.dark,
      fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
      "::placeholder": { color: "#9CA3AF" },
    },
    invalid: { color: C.danger },
  },
  hidePostalCode: true,
};

/* ─── Checkout form ───────────────────────────────────────────────────── */
const RefillCheckoutForm = ({ packageId, amountUsd, smsQty, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [cardError, setCardError] = useState(null);

  const handlePay = async () => {
    if (!stripe || !elements) return;
    setProcessing(true);
    setCardError(null);

    try {
      const businessId = getAdminBusinessId();
      const body = { business_id: businessId };
      if (packageId) body.package_id = packageId;
      else body.amount_usd = amountUsd;

      const piRes = await axiosInstance.post("admin/sms-balance/create-refill-intent", body);
      const payload = piRes.data?.data || {};
      const { client_secret, payment_intent_id } = payload;

      if (!client_secret) throw new Error("Could not start payment. Please try again.");

      const { error, paymentIntent } = await stripe.confirmCardPayment(client_secret, {
        payment_method: { card: elements.getElement(CardElement) },
      });

      if (error) {
        setCardError(error.message);
        setProcessing(false);
        return;
      }

      if (paymentIntent?.status === "succeeded") {
        const confirmRes = await axiosInstance.post("admin/sms-balance/confirm-refill", {
          payment_intent_id,
          business_id: businessId,
        });
        onSuccess(confirmRes.data?.data?.overview);
      } else {
        setCardError("Payment not confirmed. Your balance was not changed. Please retry.");
      }
    } catch (err) {
      setCardError(
        err?.response?.data?.message || err?.message || "Payment failed. Balance unchanged."
      );
    }
    setProcessing(false);
  };

  return (
    <div style={{ marginTop: 16 }}>
      <div
        style={{
          background: C.blueSoft,
          borderRadius: 10,
          padding: "12px 14px",
          marginBottom: 14,
          fontSize: 13,
          color: C.blueText,
        }}
      >
        <strong>Checkout summary</strong>
        <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 4 }}>
          <span>Amount: <strong>{fmtUsd(amountUsd)}</strong></span>
          <span>SMS quantity: <strong>{fmtSms(smsQty)} SMS</strong></span>
          <span>Tax: <strong>Calculated at payment</strong></span>
          <span>Total: <strong>{fmtUsd(amountUsd)}</strong> + applicable tax</span>
        </div>
      </div>

      <div style={{ fontSize: 13, fontWeight: 600, color: C.mid, marginBottom: 8 }}>Card details</div>
      <div
        style={{
          border: `1px solid ${C.border}`,
          borderRadius: 10,
          padding: "14px 16px",
          background: C.white,
          marginBottom: 12,
        }}
      >
        <CardElement options={CARD_OPTS} onChange={(e) => e.error && setCardError(e.error.message)} />
      </div>
      {cardError && (
        <div style={{ color: C.danger, fontSize: 13, marginBottom: 10 }}>{cardError}</div>
      )}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={onCancel}
          disabled={processing}
          style={{
            padding: "11px 20px",
            borderRadius: 10,
            border: `1px solid ${C.border}`,
            background: C.white,
            color: C.mid,
            fontWeight: 600,
            cursor: processing ? "not-allowed" : "pointer",
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handlePay}
          disabled={processing || !stripe}
          style={{
            padding: "11px 22px",
            borderRadius: 10,
            border: "none",
            background: processing ? "#D1D5DB" : C.orange,
            color: C.white,
            fontWeight: 700,
            cursor: processing ? "not-allowed" : "pointer",
          }}
        >
          {processing ? "Processing…" : `Pay ${fmtUsd(amountUsd)}`}
        </button>
      </div>
      <div style={{ marginTop: 10, fontSize: 12, color: C.muted }}>
        🔒 Secure payment · Funds are added only after payment succeeds
      </div>
    </div>
  );
};

/* ─── Main tab ────────────────────────────────────────────────────────── */
class SMSBalanceTab extends Component {
  state = {
    loading: true,
    error: null,
    overview: null,
    selectedPackageId: null,
    customMode: false,
    customUsd: "",
    showCheckout: false,
    successMsg: null,
    failMsg: null,
    autoEnabled: false,
    autoThreshold: "5",
    autoPackageId: null,
    savingAuto: false,
    autoMsg: null,
  };

  manualRef = React.createRef();

  componentDidMount() {
    this.loadOverview();
  }

  loadOverview = () => {
    this.setState({ loading: true, error: null });
    const businessId = getAdminBusinessId();
    const url = businessId
      ? `admin/sms-balance?business_id=${businessId}`
      : "admin/sms-balance";

    axiosInstance
      .get(url)
      .then((res) => {
        const overview = res.data?.data || null;
        const packages = overview?.packages || overview?.presets || [];
        const defaultPkg =
          packages.find((p) => Number(p.usd) === 25) ||
          packages[0] ||
          null;
        const autoPkgId =
          overview?.auto_refill?.package_id ||
          (overview?.auto_refill?.amount_usd
            ? packages.find((p) => Number(p.usd) === Number(overview.auto_refill.amount_usd))?.id
            : null) ||
          defaultPkg?.id ||
          null;

        this.setState({
          overview,
          loading: false,
          selectedPackageId: defaultPkg?.id || null,
          autoEnabled: !!overview?.auto_refill?.enabled,
          autoThreshold: String(overview?.auto_refill?.threshold_usd ?? 5),
          autoPackageId: autoPkgId,
        });
      })
      .catch((err) => {
        this.setState({
          loading: false,
          error: err?.response?.data?.message || "Could not load SMS balance.",
        });
      });
  };

  smsPerUsd = () => Number(this.state.overview?.rate?.sms_per_usd || 75);

  smsForUsd = (usd) => Math.round(Number(usd || 0) * this.smsPerUsd());

  selectedPackage = () => {
    const packages = this.state.overview?.packages || this.state.overview?.presets || [];
    return packages.find((p) => p.id === this.state.selectedPackageId) || null;
  };

  effectiveUsd = () => {
    const { customMode, customUsd } = this.state;
    if (customMode) {
      const n = parseFloat(customUsd);
      return Number.isFinite(n) ? n : 0;
    }
    return Number(this.selectedPackage()?.usd || 0);
  };

  effectiveSms = () => {
    const { customMode } = this.state;
    if (customMode) return this.smsForUsd(this.effectiveUsd());
    return Number(this.selectedPackage()?.sms || 0);
  };

  selectPackage = (pkg) => {
    this.setState({
      selectedPackageId: pkg.id,
      customMode: false,
      customUsd: "",
      showCheckout: false,
      failMsg: null,
    });
  };

  selectCustom = () => {
    this.setState({ customMode: true, selectedPackageId: null, showCheckout: false, failMsg: null });
  };

  scrollToManual = () => {
    if (this.manualRef.current) {
      this.manualRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  startCheckout = () => {
    const { customMode, selectedPackageId, overview } = this.state;
    const minUsd = Number(overview?.min_refill_usd || 10);
    const usd = this.effectiveUsd();

    if (!customMode && !selectedPackageId) {
      this.setState({ failMsg: "Select a package amount to continue." });
      return;
    }
    if (usd < minUsd) {
      this.setState({ failMsg: `Minimum refill is ${fmtUsd(minUsd)}.` });
      return;
    }
    this.setState({ showCheckout: true, failMsg: null, successMsg: null });
  };

  onRefillSuccess = (overview) => {
    this.setState({
      showCheckout: false,
      successMsg: "Refill successful. Funds were added to your SMS balance.",
      failMsg: null,
      overview: overview || this.state.overview,
    });
    this.loadOverview();
  };

  saveAutoRefill = () => {
    const { autoEnabled, autoThreshold, autoPackageId } = this.state;
    const threshold = parseFloat(autoThreshold);
    const packages = this.state.overview?.packages || [];
    const pkg = packages.find((p) => p.id === autoPackageId);

    if (autoEnabled) {
      if (!Number.isFinite(threshold) || threshold < 5) {
        this.setState({ autoMsg: "Threshold must be at least US$5. Custom thresholds must be greater than US$5." });
        return;
      }
      if (!pkg) {
        this.setState({ autoMsg: "Select an automatic refill package." });
        return;
      }
    }

    this.setState({ savingAuto: true, autoMsg: null });
    axiosInstance
      .put("admin/sms-balance/auto-refill", {
        enabled: autoEnabled,
        threshold_usd: Number.isFinite(threshold) ? threshold : 5,
        package_id: pkg?.id || null,
        amount_usd: pkg ? pkg.usd : undefined,
        business_id: getAdminBusinessId(),
      })
      .then((res) => {
        const overview = res.data?.data?.overview;
        this.setState({
          savingAuto: false,
          autoMsg: "Auto-refill settings saved.",
          overview: overview || this.state.overview,
        });
        this.loadOverview();
      })
      .catch((err) => {
        this.setState({
          savingAuto: false,
          autoMsg: err?.response?.data?.message || "Could not save auto-refill settings.",
        });
      });
  };

  renderProgress = (balanceUsd, thresholdUsd) => {
    const max = Math.max(20, balanceUsd * 1.2, thresholdUsd * 2);
    const pct = Math.min(100, Math.max(0, (balanceUsd / max) * 100));
    const thrPct = Math.min(100, (thresholdUsd / max) * 100);
    const status = balanceUsd <= 0 ? "Exhausted" : balanceUsd < thresholdUsd ? "Low" : "Healthy";
    const statusColor =
      status === "Healthy" ? C.green : status === "Low" ? C.orange : C.danger;

    return (
      <div style={{ minWidth: 220, flex: "1 1 240px" }}>
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: statusColor }}>{status}</span>
        </div>
        <div
          style={{
            position: "relative",
            height: 10,
            background: "rgba(255,255,255,0.2)",
            borderRadius: 999,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${pct}%`,
              height: "100%",
              background: status === "Healthy" ? C.green : C.orange,
              borderRadius: 999,
            }}
          />
          <div
            style={{
              position: "absolute",
              left: `${thrPct}%`,
              top: -3,
              width: 2,
              height: 16,
              background: C.orange,
            }}
            title={`Refills below ${fmtUsd(thresholdUsd)}`}
          />
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: 8,
            fontSize: 11,
            color: "rgba(255,255,255,0.75)",
          }}
        >
          <span>US$0</span>
          <span>Refills below {fmtUsd(thresholdUsd)}</span>
          <span>{fmtUsd(max)}</span>
        </div>
      </div>
    );
  };

  renderActivityRow = (tx) => {
    const isCredit = !!tx.is_credit;
    const amountColor = isCredit ? C.green : C.danger;
    const amountPrefix = isCredit ? "+ " : "− ";
    const statusBg = tx.status === "processed" ? C.blueBadge : C.greenBg;
    const statusColor = tx.status === "processed" ? C.blueBadgeText : C.greenText;
    const statusLabel =
      tx.status === "processed"
        ? "Processed"
        : tx.status === "completed"
        ? "Completed"
        : tx.status;

    const iconBg =
      tx.category === "auto_refill"
        ? C.blueSoft
        : tx.category === "sms_usage"
        ? "#F3F4F6"
        : C.orangeSoft;

    return (
      <tr key={tx.id}>
        <td style={{ padding: "14px 12px", borderBottom: `1px solid ${C.border}` }}>
          <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
            <div
              style={{
                width: 34,
                height: 34,
                borderRadius: 10,
                background: iconBg,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 14,
                flexShrink: 0,
              }}
            >
              {tx.category === "auto_refill" ? "↻" : tx.category === "sms_usage" ? "☰" : "+"}
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: C.dark }}>{tx.title}</div>
              <div style={{ fontSize: 12, color: C.muted }}>{tx.subtitle}</div>
            </div>
          </div>
        </td>
        <td style={{ padding: "14px 12px", borderBottom: `1px solid ${C.border}`, fontSize: 13, color: C.mid }}>
          {tx.date ? new Date(tx.date).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—"}
        </td>
        <td style={{ padding: "14px 12px", borderBottom: `1px solid ${C.border}`, fontSize: 13, color: C.mid }}>
          {tx.method || "—"}
        </td>
        <td
          style={{
            padding: "14px 12px",
            borderBottom: `1px solid ${C.border}`,
            fontSize: 13,
            fontWeight: 700,
            color: amountColor,
          }}
        >
          {tx.amount_usd != null ? `${amountPrefix}${fmtUsd(tx.amount_usd)}` : "—"}
        </td>
        <td style={{ padding: "14px 12px", borderBottom: `1px solid ${C.border}`, fontSize: 13, color: C.mid }}>
          {tx.balance_after_usd != null ? fmtUsd(tx.balance_after_usd) : "—"}
        </td>
        <td style={{ padding: "14px 12px", borderBottom: `1px solid ${C.border}` }}>
          <span
            style={{
              display: "inline-block",
              padding: "3px 10px",
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 600,
              background: statusBg,
              color: statusColor,
            }}
          >
            {statusLabel}
          </span>
        </td>
      </tr>
    );
  };

  render() {
    const {
      loading,
      error,
      overview,
      selectedPackageId,
      customMode,
      customUsd,
      showCheckout,
      successMsg,
      failMsg,
      autoEnabled,
      autoThreshold,
      autoPackageId,
      savingAuto,
      autoMsg,
    } = this.state;

    if (loading) {
      return (
        <div style={{ padding: 28, color: C.muted, fontSize: 14 }}>Loading SMS balance…</div>
      );
    }

    if (error) {
      return (
        <div style={{ padding: 28 }}>
          <div style={{ color: C.danger, marginBottom: 12 }}>{error}</div>
          <button type="button" onClick={this.loadOverview} style={{ color: C.orange, fontWeight: 600, border: "none", background: "none", cursor: "pointer" }}>
            Retry
          </button>
        </div>
      );
    }

    const balanceUsd = overview?.balance_usd ?? 0;
    const estimatedSms = overview?.estimated_sms ?? 0;
    const auto = overview?.auto_refill || {};
    const thresholdUsd = auto.threshold_usd ?? 5;
    const amountUsdAuto = auto.amount_usd ?? 25;
    const pm = overview?.payment_method;
    const packages = overview?.packages || overview?.presets || [];
    const rate = overview?.rate || {};
    const minUsd = Number(overview?.min_refill_usd || 10);
    const effectiveUsd = this.effectiveUsd();
    const effectiveSms = this.effectiveSms();
    const transactions = overview?.transactions || [];
    const autoPkg = packages.find((p) => p.id === autoPackageId);

    return (
      <div style={{ padding: "24px" }}>
        {/* Header actions */}
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 14 }}>
          <button
            type="button"
            onClick={this.scrollToManual}
            style={{
              background: C.orange,
              color: C.white,
              border: "none",
              borderRadius: 10,
              padding: "11px 18px",
              fontWeight: 700,
              fontSize: 14,
              cursor: "pointer",
              boxShadow: "0 8px 20px rgba(249,115,22,0.25)",
            }}
          >
            + Refill balance
          </button>
        </div>

        {/* Hero balance card */}
        <div
          style={{
            background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`,
            borderRadius: 18,
            padding: "28px 28px 22px",
            color: C.white,
            marginBottom: 22,
            boxShadow: "0 12px 30px rgba(11,58,92,0.25)",
          }}
        >
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 28,
              alignItems: "flex-start",
              justifyContent: "space-between",
            }}
          >
            <div style={{ flex: "1 1 260px" }}>
              <div style={{ fontSize: 13, opacity: 0.85, marginBottom: 6 }}>Available balance</div>
              <div style={{ fontSize: 40, fontWeight: 800, letterSpacing: "-0.03em", lineHeight: 1.1 }}>
                {fmtUsd(balanceUsd)}
              </div>
              <div style={{ marginTop: 10, fontSize: 14, opacity: 0.9 }}>
                Enough to send approximately <strong>{fmtSms(estimatedSms)} SMS</strong>.
              </div>
              <div style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: auto.enabled ? C.orange : "#94A3B8",
                    display: "inline-block",
                  }}
                />
                <span style={{ opacity: 0.95 }}>
                  {auto.enabled ? "Auto-refill is active" : "Auto-refill is off"}
                </span>
              </div>
            </div>

            {this.renderProgress(balanceUsd, thresholdUsd)}
          </div>

          {auto.enabled && (
            <div
              style={{
                marginTop: 18,
                background: "rgba(232,244,252,0.16)",
                border: "1px solid rgba(255,255,255,0.18)",
                borderRadius: 12,
                padding: "12px 14px",
                fontSize: 13,
                display: "flex",
                gap: 10,
                alignItems: "center",
              }}
            >
              <span style={{ fontSize: 16 }}>↻</span>
              <span>
                <strong>Automatic refill:</strong> {fmtUsd(amountUsdAuto)} will be added below{" "}
                {fmtUsd(thresholdUsd)}
              </span>
            </div>
          )}
        </div>

        {successMsg && (
          <div
            style={{
              background: C.greenBg,
              color: C.greenText,
              borderRadius: 12,
              padding: "12px 16px",
              marginBottom: 16,
              fontWeight: 600,
              fontSize: 14,
            }}
          >
            {successMsg}
          </div>
        )}
        {failMsg && (
          <div
            style={{
              background: C.dangerBg,
              color: "#991B1B",
              borderRadius: 12,
              padding: "12px 16px",
              marginBottom: 16,
              fontWeight: 600,
              fontSize: 14,
            }}
          >
            {failMsg}
          </div>
        )}

        {/* Manual + Auto columns */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
            gap: 20,
            marginBottom: 22,
          }}
        >
          {/* Manual refill */}
          <div
            ref={this.manualRef}
            style={{
              background: C.white,
              border: `1px solid ${C.border}`,
              borderRadius: 16,
              padding: 24,
            }}
          >
            <div style={{ fontSize: 18, fontWeight: 700, color: C.dark, marginBottom: 4 }}>
              Manual refill
            </div>
            <div style={{ fontSize: 13, color: C.muted, marginBottom: 18 }}>
              Add funds to your SMS balance at any time.
            </div>

            <div style={{ fontSize: 15, fontWeight: 700, color: C.navy, marginBottom: 10 }}>
              Choose an amount
            </div>

            {packages.length === 0 ? (
              <div
                style={{
                  background: C.dangerBg,
                  color: "#991B1B",
                  borderRadius: 10,
                  padding: "12px 14px",
                  marginBottom: 14,
                  fontSize: 13,
                }}
              >
                No SMS packages are configured yet. Ask a super admin to add packages on the SMS Packages page.
              </div>
            ) : (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
                {packages.map((p) => {
                  const selected = !customMode && selectedPackageId === p.id;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => this.selectPackage(p)}
                      style={{
                        minWidth: 92,
                        padding: "12px 10px",
                        borderRadius: 12,
                        border: `1.5px solid ${selected ? C.orange : C.border}`,
                        background: selected ? C.orangeSoft : C.white,
                        boxShadow: selected ? `0 0 0 1px ${C.orange}` : "none",
                        cursor: "pointer",
                        textAlign: "left",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 14,
                          fontWeight: 800,
                          color: selected ? C.orange : C.navy,
                        }}
                      >
                        {fmtUsd(p.usd).replace("US$ ", "US$")}
                      </div>
                      <div style={{ fontSize: 11, color: selected ? C.orange : C.muted, marginTop: 2 }}>
                        {fmtSms(p.sms)} SMS
                      </div>
                    </button>
                  );
                })}
                <button
                  type="button"
                  onClick={this.selectCustom}
                  style={{
                    minWidth: 92,
                    padding: "12px 10px",
                    borderRadius: 12,
                    border: `1.5px solid ${customMode ? C.orange : C.border}`,
                    background: customMode ? C.orangeSoft : C.white,
                    boxShadow: customMode ? `0 0 0 1px ${C.orange}` : "none",
                    cursor: "pointer",
                    fontWeight: 700,
                    color: customMode ? C.orange : C.navy,
                  }}
                >
                  Custom
                </button>
              </div>
            )}

            {customMode && (
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: C.mid }}>
                  Custom amount (min {fmtUsd(minUsd)})
                </label>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
                  <span style={{ color: C.muted, fontWeight: 600 }}>US$</span>
                  <input
                    type="number"
                    min={minUsd}
                    step="0.01"
                    value={customUsd}
                    onChange={(e) => this.setState({ customUsd: e.target.value, showCheckout: false })}
                    style={{
                      flex: 1,
                      border: `1px solid ${C.border}`,
                      borderRadius: 10,
                      padding: "10px 12px",
                      fontSize: 14,
                    }}
                    placeholder={String(minUsd)}
                  />
                </div>
              </div>
            )}

            <div
              style={{
                background: C.blueSoft,
                borderRadius: 12,
                padding: "12px 14px",
                marginBottom: 16,
                color: C.blueText,
                fontSize: 13,
              }}
            >
              <strong>
                {fmtUsd(rate.usd || minUsd)} gives you {fmtSms(rate.sms || this.smsForUsd(rate.usd || minUsd))} SMS
              </strong>
              <div style={{ marginTop: 4, opacity: 0.9 }}>
                Your selection provides approximately {fmtSms(effectiveSms)} SMS.
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: C.muted, marginBottom: 6 }}>
                Payment method
              </div>
              <div
                style={{
                  border: `1px solid ${C.border}`,
                  borderRadius: 10,
                  padding: "12px 14px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  background: C.surface,
                }}
              >
                <span style={{ fontSize: 14, color: C.dark, fontWeight: 600 }}>
                  {pm?.label || "Card will be entered at checkout"}
                </span>
                <a
                  href="/admin/manage-subscription"
                  style={{ fontSize: 13, color: C.orange, fontWeight: 600, textDecoration: "none" }}
                >
                  Change
                </a>
              </div>
            </div>

            {!showCheckout ? (
              <>
                <button
                  type="button"
                  onClick={this.startCheckout}
                  disabled={effectiveUsd < minUsd || (!customMode && !selectedPackageId)}
                  style={{
                    width: "100%",
                    padding: "14px 16px",
                    borderRadius: 12,
                    border: "none",
                    background:
                      effectiveUsd < minUsd || (!customMode && !selectedPackageId)
                        ? "#D1D5DB"
                        : C.orange,
                    color: C.white,
                    fontWeight: 800,
                    fontSize: 15,
                    cursor:
                      effectiveUsd < minUsd || (!customMode && !selectedPackageId)
                        ? "not-allowed"
                        : "pointer",
                  }}
                >
                  Get {fmtSms(effectiveSms)} SMS for {fmtUsd(effectiveUsd)}
                </button>
                <div style={{ marginTop: 10, fontSize: 12, color: C.muted, textAlign: "center" }}>
                  🔒 Secure payment · Funds are added only after payment succeeds
                </div>
              </>
            ) : (
              <Elements stripe={stripePromise}>
                <RefillCheckoutForm
                  packageId={customMode ? null : selectedPackageId}
                  amountUsd={effectiveUsd}
                  smsQty={effectiveSms}
                  onSuccess={this.onRefillSuccess}
                  onCancel={() => this.setState({ showCheckout: false })}
                />
              </Elements>
            )}
          </div>

          {/* Automatic refill */}
          <div
            style={{
              background: C.white,
              border: `1px solid ${C.border}`,
              borderRadius: 16,
              padding: 24,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: C.dark }}>Automatic refill</div>
              <button
                type="button"
                aria-label="Toggle auto-refill"
                onClick={() => this.setState({ autoEnabled: !autoEnabled, autoMsg: null })}
                style={{
                  width: 48,
                  height: 28,
                  borderRadius: 999,
                  border: "none",
                  background: autoEnabled ? C.orange : "#CBD5E1",
                  position: "relative",
                  cursor: "pointer",
                  transition: "background 0.15s",
                }}
              >
                <span
                  style={{
                    position: "absolute",
                    top: 3,
                    left: autoEnabled ? 23 : 3,
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    background: C.white,
                    transition: "left 0.15s",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                  }}
                />
              </button>
            </div>
            <div style={{ fontSize: 13, color: C.muted, marginBottom: 18, marginTop: 4 }}>
              Prevent SMS reminders from being interrupted.
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: C.mid }}>
                Refill when balance falls below
              </label>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
                <span style={{ color: C.muted, fontWeight: 600 }}>US$</span>
                <input
                  type="number"
                  min="5"
                  step="0.01"
                  value={autoThreshold}
                  disabled={!autoEnabled}
                  onChange={(e) => this.setState({ autoThreshold: e.target.value, autoMsg: null })}
                  style={{
                    flex: 1,
                    border: `1px solid ${C.border}`,
                    borderRadius: 10,
                    padding: "10px 12px",
                    fontSize: 14,
                    background: autoEnabled ? C.white : C.surface,
                  }}
                />
              </div>
              <div style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>
                US$5 is the default. Custom thresholds must be greater than US$5.
              </div>
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: C.mid }}>Amount to add</label>
              <select
                value={autoPackageId || ""}
                disabled={!autoEnabled || packages.length === 0}
                onChange={(e) =>
                  this.setState({
                    autoPackageId: e.target.value ? Number(e.target.value) : null,
                    autoMsg: null,
                  })
                }
                style={{
                  width: "100%",
                  marginTop: 6,
                  border: `1px solid ${C.border}`,
                  borderRadius: 10,
                  padding: "11px 12px",
                  fontSize: 14,
                  background: autoEnabled ? C.white : C.surface,
                }}
              >
                {packages.length === 0 && <option value="">No packages configured</option>}
                {packages.map((p) => (
                  <option key={p.id} value={p.id}>
                    {fmtUsd(p.usd)} — {fmtSms(p.sms)} SMS
                  </option>
                ))}
              </select>
              <div style={{ fontSize: 12, color: C.orange, marginTop: 6, fontWeight: 600 }}>
                {fmtSms(autoPkg?.sms || 0)} SMS will be added each time auto-refill runs.
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: C.muted, marginBottom: 6 }}>
                Payment method
              </div>
              <div
                style={{
                  border: `1px solid ${C.border}`,
                  borderRadius: 10,
                  padding: "12px 14px",
                  background: C.surface,
                  fontSize: 14,
                  fontWeight: 600,
                  color: C.dark,
                }}
              >
                {pm?.label || "No card on file — add one via subscription billing"}
              </div>
            </div>

            {autoMsg && (
              <div
                style={{
                  marginBottom: 12,
                  fontSize: 13,
                  color: autoMsg.includes("saved") ? C.greenText : "#991B1B",
                  fontWeight: 600,
                }}
              >
                {autoMsg}
              </div>
            )}

            <button
              type="button"
              onClick={this.saveAutoRefill}
              disabled={savingAuto}
              style={{
                width: "100%",
                padding: "12px 16px",
                borderRadius: 12,
                border: `1.5px solid ${C.orange}`,
                background: C.white,
                color: C.orange,
                fontWeight: 700,
                fontSize: 14,
                cursor: savingAuto ? "not-allowed" : "pointer",
              }}
            >
              {savingAuto ? "Saving…" : "Save auto-refill settings"}
            </button>
          </div>
        </div>

        {/* Recent activity */}
        <div
          style={{
            background: C.white,
            border: `1px solid ${C.border}`,
            borderRadius: 16,
            padding: 24,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: C.dark }}>Recent balance activity</div>
          </div>

          {transactions.length === 0 ? (
            <div style={{ padding: "28px 0", textAlign: "center", color: C.muted, fontSize: 14 }}>
              No balance activity yet.
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    {["Transaction", "Date", "Method", "Amount", "Balance", "Status"].map((h) => (
                      <th
                        key={h}
                        style={{
                          textAlign: "left",
                          padding: "10px 12px",
                          fontSize: 11,
                          fontWeight: 700,
                          color: C.muted,
                          textTransform: "uppercase",
                          letterSpacing: "0.04em",
                          borderBottom: `1px solid ${C.border}`,
                          background: C.surface,
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>{transactions.map((tx) => this.renderActivityRow(tx))}</tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default SMSBalanceTab;
